import { useMemo } from 'react';

import { bindActionCreators, useDispatch } from 'third-party';

import {
  userAuthEndpoint,
  userAvatarEndpoint,
  userEndpoint,
  userInfoEndpoint,
  userPasswordResetLinkEndpoint,
  userRefreshTokenEndpoint,
  userResetPasswordEndpoint,
  validateUserResetTokenPasswordEndpoint,
} from 'constants/endpoints';

import { saveUserToken } from 'utils/auth';
import { httpMethod, simplifyBuilder } from 'utils/sra';
import { getFromStorage, saveInStorage } from 'utils/storage';

import { APIUser, APIUserRegister, APIUserResetPasswordModel } from 'types/api';

export type AppState = {
  user: APIUser | null;
  userInfoPending: boolean;
  isAppLoaded: boolean;
  userLoginInPending: boolean;
  userRegisterPending: boolean;
  updateAvatarPending: boolean;
  updateSelfInfoPending: boolean;
  isNavigationCollapsed: boolean;
  deleteTenantPending: boolean;
};

export const appState: AppState = {
  user: null,
  userInfoPending: false,
  isAppLoaded: false,
  updateAvatarPending: false,
  updateSelfInfoPending: false,
  isNavigationCollapsed: getFromStorage('isNavigationCollapsed', false),
  userLoginInPending: false,
  userRegisterPending: false,
  deleteTenantPending: false,
};

const builder = simplifyBuilder(appState, {});

export const setAppIsLoaded = builder.createReduxAction(
  (isAppLoaded: boolean) => ({
    name: 'setUser',
    updater: () => ({
      isAppLoaded: isAppLoaded,
    }),
  }),
);

export const setUser = builder.createReduxAction(
  (user: APIUser | undefined) => ({
    name: 'setUser',
    updater: () => ({
      user: user,
    }),
  }),
);

const toggleNavigation = builder.createReduxAction(() => ({
  name: 'setUser',
  updater: state => {
    saveInStorage('isNavigationCollapsed', !state.isNavigationCollapsed);
    return {
      isNavigationCollapsed: !state.isNavigationCollapsed,
    };
  },
}));

export const userLogOut = builder.createReduxAction(() => ({
  name: 'userLogOut',
  updater: () => ({
    user: null,
  }),
}));

const userLogIn = builder.createServerAction(
  (email: string, password: string) => ({
    name: 'userLoginIn',
    url: userAuthEndpoint(),
    method: httpMethod.post,
    body: { email, password },
    onRequest: () => ({
      userLoginInErrorText: '',
    }),
    onSuccess: (state: AppState, payload: string) => {
      saveUserToken(payload);
    },
  }),
);

const userSendPasswordResetLink = builder.createServerAction(
  (email: string) => ({
    name: 'userSendPasswordResetLink',
    url: userPasswordResetLinkEndpoint(email),
    method: httpMethod.put,
  }),
);

const userResetPassword = builder.createServerAction(
  (model: APIUserResetPasswordModel) => ({
    name: 'userResetPassword',
    url: userResetPasswordEndpoint(),
    method: httpMethod.put,
    body: model,
    onRequest: () => ({
      userResetErrorText: '',
    }),
  }),
);

const validateUserResetPasswordToken = builder.createServerAction(
  (email: string, token: string) => ({
    name: 'validateUserResetPasswordToken',
    url: validateUserResetTokenPasswordEndpoint(email, token),
    method: httpMethod.get,
    // onSuccess: we don't put anything to store, just return result for typeahead
  }),
);

const userInfo = builder.createServerAction(() => ({
  name: 'userInfo',
  url: userInfoEndpoint(),
  method: httpMethod.get,
  onSuccess: (state: AppState, payload: APIUser) => {
    // refresh token if available
    if (payload.token) {
      saveUserToken(payload.token);
    }

    return {
      user: {
        ...payload,
        userTenants: payload.userTenants.map(it => ({
          ...it,
          isSelected: false,
        })),
      },
    };
  },
}));

const refreshTokens = builder.createServerAction(() => ({
  name: 'refreshTokens',
  url: userRefreshTokenEndpoint(),
  method: httpMethod.post,
  onSuccess: (state: AppState, payload: string) => {
    saveUserToken(payload);
  },
}));

const userRegister = builder.createServerAction((model: APIUserRegister) => ({
  name: 'userRegister',
  url: userEndpoint(),
  method: httpMethod.put,
  body: model,
  onRequest: () => ({
    userLoginInErrorText: '',
  }),
  onSuccess: (state: AppState, payload: string) => {
    saveUserToken(payload);
  },
}));

export const selectTenant = builder.createReduxAction(
  (tenantId: string | undefined) => ({
    name: 'selectTenant',
    updater: (state: AppState) => ({
      ...state,
      user: state.user
        ? {
            ...state.user,
            userTenants: state.user.userTenants?.map(it => ({
              ...it,
              isSelected: it.tenant.id === tenantId,
            })),
          }
        : null,
    }),
  }),
);

const updateAvatar = builder.createServerAction((file: any) => ({
  name: 'updateAvatar',
  url: userAvatarEndpoint(),
  method: httpMethod.put,
  body: file,
  onSuccess: (state: AppState, payload: APIUser) => {
    return {
      user: {
        ...state.user,
        avatar: payload.avatar,
      },
    };
  },
}));

const updateSelfInfo = builder.createServerAction((userModel: APIUser) => ({
  name: 'updateSelfInfo',
  url: userInfoEndpoint(),
  method: httpMethod.put,
  body: userModel,
  onSuccess: (state: AppState, payload: APIUser) => {
    return {
      user: {
        ...state.user,
        firstName: payload.firstName,
        lastName: payload.lastName,
        phone: payload.phone,
      },
    };
  },
}));

export const useApp = () => {
  const dispatch = useDispatch();

  return useMemo(
    () =>
      bindActionCreators(
        {
          refreshTokens,
          selectTenant,
          userRegister,
          userSendPasswordResetLink,
          validateUserResetPasswordToken,
          userResetPassword,
          setUser,
          updateAvatar,
          updateSelfInfo,
          userInfo,
          toggleNavigation,
          setAppIsLoaded,
          userLogOut,
          userLogIn,
        },
        dispatch,
      ),
    [dispatch],
  );
};

export const appReducer = builder.getReducers();
